import { useCallback, useMemo } from 'react'
import { Controller, FieldError, useFormContext, useFormState, useWatch } from 'react-hook-form'
import { renameSWxxToCxxIfApplicable } from '@domainUtils'
import { get } from 'lodash-es'
import { Box, LinearProgress, MenuItem, SelectProps, TextField, Typography } from '@mui/material'
import { usageClassOptions } from '@editor/constants'
import {
  crossSectionForMaterialCategory,
  defaultMaterialForMaterialKind,
} from '@editor/pages/Results2/components/Results2DetailPane/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CompactField } from '@ui2/structure'
import { CentimeterField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import {
  getGluelamMaterials,
  getLvlMaterials,
  getSoftWoodMaterials,
  getSteelCSShapes,
  getSteelMaterials,
} from '@queries'
import SteelShapeCompactSelect from '../../pages/StructuralPlanning/components/MultiEdit/components/MultiEditCrossSection/components/SteelShapeCompactSelect/indes'
import { materialOptions } from './materialOptions'
import {
  cmAdornment,
  compactFieldSx,
  materialCategoryFromCrossSection,
  materialRowKey,
  materialSelectSx,
} from './utils'

const layerThicknessTooltip = { text: 'Bestimmt Schichtdicke' } as const

interface Props {
  crossSectionFieldPath: string
  onChangeMaterialCategory: (
    currentCs: CrossSectionTimberOrCLTOrSteel,
    nextCs: CrossSectionTimberOrCLTOrSteel,
  ) => Promise<unknown>
  disabled: boolean
  elementType?: ElementTypes
}

const CrossSectionInput = ({
  crossSectionFieldPath,
  onChangeMaterialCategory,
  disabled,
  elementType,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  const crossSection = useWatch({ name: crossSectionFieldPath }) as
    | CrossSectionTimberOrCLTOrSteel
    | null
    | undefined

  const materialCategory = useMemo((): MaterialKind => {
    if (!crossSection) return 'Softwood'
    if (crossSection.kind === 'CLT') return 'Softwood'
    return materialCategoryFromCrossSection(crossSection as CrossSection)
  }, [crossSection])
  const isSteel = crossSection?.kind === 'Steel'

  const {
    data: softwoodMaterials,
    isLoading: softwoodMaterialsLoading,
    error: softwoodMaterialsQueryError,
  } = useQuery<Material[]>(getSoftWoodMaterials.key, getSoftWoodMaterials.request)

  const {
    data: glulamMaterials,
    isLoading: glulamMaterialsLoading,
    error: glulamMaterialsQueryError,
  } = useQuery<Material[]>(getGluelamMaterials.key, getGluelamMaterials.request)

  const {
    data: lvlMaterials,
    isLoading: lvlMaterialsLoading,
    error: lvlMaterialsQueryError,
  } = useQuery<Material[]>(getLvlMaterials.key, getLvlMaterials.request)

  const {
    data: steelMaterials,
    isLoading: steelMaterialsLoading,
    error: steelMaterialsQueryError,
  } = useQuery<SteelMaterial[]>(getSteelMaterials.key, getSteelMaterials.request)

  const {
    data: steelShapes,
    isLoading: steelShapesLoading,
    error: steelShapesQueryError,
  } = useQuery<SteelCSShape[]>(getSteelCSShapes.key, getSteelCSShapes.request)

  const materialsLoading =
    softwoodMaterialsLoading ||
    glulamMaterialsLoading ||
    lvlMaterialsLoading ||
    steelMaterialsLoading
  const materialsQueryError =
    softwoodMaterialsQueryError ||
    glulamMaterialsQueryError ||
    lvlMaterialsQueryError ||
    steelMaterialsQueryError

  const getMaterialsByKind = useCallback(
    (kind: MaterialKind) => {
      switch (kind) {
        case 'Softwood':
          return softwoodMaterials
        case 'Glulam':
          return glulamMaterials
        case 'LVL':
          return lvlMaterials
        case 'Steel':
          return steelMaterials
        default:
          return []
      }
    },
    [softwoodMaterials, glulamMaterials, lvlMaterials, steelMaterials],
  )

  const selectedMaterialKey = useMemo(() => {
    if (!crossSection || crossSection.kind === 'CLT') return ''
    const m = (crossSection as TimberCrossSection | SteelCrossSection).material
    if (!m) return ''
    return materialRowKey(m)
  }, [crossSection])

  const { errors } = useFormState()
  const crossSectionErrorMessage = (get(errors, crossSectionFieldPath) as FieldError | undefined)
    ?.message

  const { setValue, getValues } = useFormContext()

  const { mutate: updateMaterialCategory, isLoading: isUpdatingMaterialCategory } = useMutation(
    async (nextKind: MaterialKind) => {
      const materialsList = getMaterialsByKind(nextKind)
      const defaultMaterial = defaultMaterialForMaterialKind(materialsList ?? [], nextKind)
      if (!defaultMaterial) {
        enqueueSnackbar('Keine Materialeinträge geladen.', { variant: 'warning' })
        return
      }

      let firstSteelShape: SteelCSShape | undefined
      if (nextKind === 'Steel') {
        firstSteelShape = steelShapes?.[0]
        if (!steelShapes?.length) {
          enqueueSnackbar('Keine Stahlprofile geladen.', { variant: 'warning' })
        }
      }

      const currentCs = get(getValues(), crossSectionFieldPath)

      const nextCs = crossSectionForMaterialCategory(
        currentCs,
        nextKind,
        defaultMaterial,
        firstSteelShape,
      )

      await onChangeMaterialCategory(currentCs, nextCs)

      setValue(crossSectionFieldPath, nextCs, { shouldDirty: true, shouldTouch: true })
    },
  )

  const inputsLocked =
    disabled || isUpdatingMaterialCategory || materialsLoading || steelShapesLoading

  const selectedMaterialsList = useMemo(() => {
    if (!crossSection) return []
    if ('material' in crossSection) return getMaterialsByKind(crossSection.material.kind)
    return []
  }, [crossSection, getMaterialsByKind])

  return (
    <Box>
      {isUpdatingMaterialCategory ? (
        <LinearProgress sx={{ height: 2, borderRadius: 1, mb: 1 }} data-cy="crosssection-loading" />
      ) : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <CompactField label="Materialtyp" minWidth={140}>
          <TextField
            select
            size="small"
            value={materialCategory}
            disabled={inputsLocked}
            onChange={e => {
              updateMaterialCategory(e.target.value as MaterialKind)
            }}
            fullWidth
            sx={compactFieldSx}
            slotProps={{
              select: { 'data-cy': 'crosssection-material-type-dropdown' } as SelectProps & {
                'data-cy': string
              },
            }}
          >
            {materialOptions.map(({ value, label }, i) => (
              <MenuItem
                key={value}
                value={value}
                dense
                data-cy={`crosssection-material-type-item-${i}`}
              >
                {label}
              </MenuItem>
            ))}
          </TextField>
        </CompactField>
        <CompactField label="Material" minWidth={140}>
          <TextField
            select
            size="small"
            disabled={inputsLocked || materialsLoading}
            value={
              selectedMaterialsList?.some(m => materialRowKey(m) === selectedMaterialKey)
                ? selectedMaterialKey
                : ''
            }
            onChange={e => {
              const key = e.target.value
              const picked = selectedMaterialsList?.find(m => materialRowKey(m) === key)
              if (picked) {
                setValue(`${crossSectionFieldPath}.material`, picked, {
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
            }}
            fullWidth
            sx={materialSelectSx}
            helperText={
              materialsQueryError || steelShapesQueryError
                ? 'Daten konnten nicht geladen werden'
                : undefined
            }
            FormHelperTextProps={{ sx: { mx: 0, mt: 0.25, fontSize: 11 } }}
            slotProps={{
              select: {
                displayEmpty: true,
                'data-cy': 'crosssection-material',
                renderValue: selected => {
                  if (!selected) {
                    return (
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {materialsLoading ? 'Lädt…' : 'Material auswählen'}
                      </Box>
                    )
                  }
                  const row = selectedMaterialsList?.find(m => materialRowKey(m) === selected)
                  const id = row?.identifier ?? ''
                  return renameSWxxToCxxIfApplicable(id)
                },
              } as SelectProps & { 'data-cy': string },
            }}
          >
            {(selectedMaterialsList ?? []).map((m, i) => (
              <MenuItem
                key={materialRowKey(m)}
                value={materialRowKey(m)}
                dense
                data-cy={`crosssection-material-item-${i}`}
              >
                {renameSWxxToCxxIfApplicable(m.identifier)}
              </MenuItem>
            ))}
          </TextField>
        </CompactField>
        {!isSteel && crossSection?.kind === 'Timber' ? (
          <>
            <CompactField
              label="QS Breite"
              minWidth={80}
              tooltip={elementType === 'lintels' ? layerThicknessTooltip : undefined}
            >
              <CentimeterField
                name={`${crossSectionFieldPath}.shape.width`}
                disabled={inputsLocked}
                hideAdornment
                twoDecimalPlaces
                formControlFullWidth
                sx={{ width: '100%', ...compactFieldSx }}
                InputProps={{ endAdornment: cmAdornment }}
              />
            </CompactField>
            <CompactField
              label="QS Höhe"
              minWidth={80}
              tooltip={elementType === 'rips' ? layerThicknessTooltip : undefined}
            >
              <CentimeterField
                name={`${crossSectionFieldPath}.shape.height`}
                disabled={inputsLocked}
                hideAdornment
                twoDecimalPlaces
                formControlFullWidth
                sx={{ width: '100%', ...compactFieldSx }}
                InputProps={{ endAdornment: cmAdornment }}
              />
            </CompactField>
            <CompactField label="Nutzungsklasse" minWidth={100}>
              <Controller
                name={`${crossSectionFieldPath}.usage_class`}
                render={({ field }) => (
                  <TextField
                    select
                    size="small"
                    disabled={inputsLocked}
                    value={field.value ?? 'One'}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    fullWidth
                    sx={compactFieldSx}
                  >
                    {usageClassOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value} dense>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </CompactField>
          </>
        ) : null}
        {isSteel ? (
          <CompactField label="Stahlprofil" minWidth={220}>
            <SteelShapeCompactSelect
              disabled={inputsLocked}
              crossSectionFieldPath={crossSectionFieldPath}
              data-cy="crosssection-steel-shape-select"
            />
          </CompactField>
        ) : null}
      </Box>
      {crossSectionErrorMessage ? (
        <Typography
          sx={{
            mt: 1,
            fontSize: 11,
            lineHeight: 1.35,
            color: 'error.main',
            fontFamily: 'Roboto',
          }}
        >
          {crossSectionErrorMessage}
        </Typography>
      ) : null}
    </Box>
  )
}

export default CrossSectionInput
